import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatAuthError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Sparkles, Bot, Cpu } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", instruction: "", active: true };

export default function AIRules() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, mode: "create", form: EMPTY, id: null });
  // v84.127 — Claude toggle dihapus. Single-provider Gemini via GEMINI_API_KEY.
  const [provider, setProvider] = useState(null);   // {value, options}

  async function load() {
    setLoading(true);
    try {
      const [rules, prov] = await Promise.all([
        api.listAIRules(),
        api.getAIProvider().catch(() => null),
      ]);
      setItems(rules);
      setProvider(prov);
    }
    catch (e) { toast.error(formatAuthError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    setDlg({ open: true, mode: "create", form: {...EMPTY}, id: null });
  }
  function openEdit(r) {
    setDlg({
      open: true, mode: "edit", id: r.id,
      form: { title: r.title || "", instruction: r.instruction || "", active: !!r.active }
    });
  }
  function closeDlg() { setDlg(d => ({ ...d, open: false })); }

  async function save() {
    const payload = {
      title: dlg.form.title || null,
      instruction: (dlg.form.instruction || "").trim(),
      active: !!dlg.form.active,
    };
    if (!payload.instruction) { toast.error("Instruksi tidak boleh kosong"); return; }
    if (payload.instruction.length > 2000) { toast.error("Instruksi maksimal 2000 karakter"); return; }
    try {
      if (dlg.mode === "create") {
        await api.createAIRule(payload);
        toast.success("AI Rule ditambahkan");
      } else {
        await api.updateAIRule(dlg.id, payload);
        toast.success("AI Rule diperbarui");
      }
      closeDlg();
      load();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  async function toggleActive(r) {
    try {
      await api.updateAIRule(r.id, { active: !r.active });
      toast.success(!r.active ? "Aturan diaktifkan" : "Aturan dinonaktifkan");
      load();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  async function del(r) {
    if (!window.confirm(`Hapus aturan${r.title ? ` "${r.title}"` : ""}? Aksi ini tidak bisa dibatalkan.`)) return;
    try {
      await api.deleteAIRule(r.id);
      toast.success("Aturan dihapus");
      load();
    } catch (e) { toast.error(formatAuthError(e)); }
  }

  const activeCount = items.filter(i => i.active).length;

  return (
    <div className="space-y-4" data-testid="ai-rules-page">
      {/* v84.127 — Claude toggle dihapus. Sekarang single-provider Gemini via GEMINI_API_KEY (own key). */}
      {provider && (
        <div className="border-2 border-emerald-700 bg-emerald-50/50 rounded-sm p-4" data-testid="ai-provider-toggle">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-9 h-9 rounded-sm bg-emerald-700 text-white grid place-items-center shrink-0">
              <Cpu className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-emerald-800 text-[13px]" data-testid="ai-provider-active-label">
                AI Agent Aktif: {(provider.options || []).find(o => o.value === provider.value)?.label || provider.value}
              </div>
              <div className="text-[11px] text-zinc-600 mt-0.5">
                Model tunggal via Google API key sendiri (bukan Universal Key).
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="border border-[#7C3AED]/30 bg-[#7C3AED]/5 rounded-sm p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#7C3AED] text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 text-[13px]">
          <div className="font-semibold text-[#7C3AED]">Dynamic AI Rules</div>
          <div className="text-zinc-600 mt-0.5 leading-relaxed">
            Aturan aktif di halaman ini otomatis di-inject ke system prompt AI setiap kali AI membalas customer via WhatsApp/Simulator. Tidak perlu redeploy — perubahan berlaku langsung untuk percakapan berikutnya.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[12px] text-zinc-500">
          Total: <b className="font-mono text-zinc-800">{items.length}</b> aturan
          {" · "}
          <span className="text-emerald-700 font-mono">{activeCount}</span> aktif
          {" · "}
          <span className="text-zinc-400 font-mono">{items.length - activeCount}</span> nonaktif
        </div>
        <Button onClick={openCreate} className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5" data-testid="ai-rule-add-btn">
          <Plus className="w-4 h-4" /> Tambah Aturan
        </Button>
      </div>

      {loading && <div className="text-sm text-zinc-500">Memuat…</div>}

      {!loading && items.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center text-zinc-500" data-testid="ai-rules-empty">
          <Bot className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div className="text-[14px] font-semibold">Belum ada aturan AI</div>
          <div className="text-[12px] mt-1">Klik <b>Tambah Aturan</b> untuk membuat instruksi kustom pertama Anda.</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2">
        {items.map((r, idx) => (
          <div
            key={r.id}
            className={`border rounded-sm p-3.5 flex items-start gap-3 ${r.active ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-zinc-50/40"}`}
            data-testid={`ai-rule-row-${r.id}`}
          >
            <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 mt-0.5 font-mono text-[10px] font-bold ${r.active ? "bg-emerald-600 text-white" : "bg-zinc-300 text-zinc-600"}`}>
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-[13px] truncate">
                  {r.title || <span className="text-zinc-400 italic">Tanpa judul</span>}
                </div>
                <span
                  className={`text-[9px] rounded-sm px-1.5 py-0.5 uppercase tracking-widest font-bold ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`}
                  data-testid={`ai-rule-status-${r.id}`}
                >
                  {r.active ? "Aktif" : "Nonaktif"}
                </span>
              </div>
              <div className="text-[12px] text-zinc-700 mt-1 whitespace-pre-wrap break-words leading-relaxed">
                {r.instruction}
              </div>
              <div className="text-[10px] text-zinc-400 mt-1.5 font-mono">
                {r.created_by} · {r.created_at?.slice(0,10)}
                {r.updated_at && r.updated_at !== r.created_at && (
                  <> · diperbarui {r.updated_at.slice(0,10)}</>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={!!r.active}
                  onCheckedChange={() => toggleActive(r)}
                  data-testid={`ai-rule-toggle-${r.id}`}
                />
              </div>
              <Button size="icon" variant="ghost" onClick={() => openEdit(r)}
                className="h-7 w-7 rounded-sm text-zinc-500 hover:text-[#E65100]"
                data-testid={`ai-rule-edit-${r.id}`}
                title="Edit aturan">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => del(r)}
                className="h-7 w-7 rounded-sm text-zinc-400 hover:text-red-500"
                data-testid={`ai-rule-delete-${r.id}`}
                title="Hapus aturan">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dlg.open} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="ai-rule-dialog">
          <DialogHeader>
            <DialogTitle>{dlg.mode === "create" ? "Tambah AI Rule" : "Edit AI Rule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Judul (opsional)</label>
              <Input
                placeholder="mis. Sapa Ramah, Promo Diskon, Batasi Servis Luar Kota"
                value={dlg.form.title}
                onChange={e => setDlg(d => ({...d, form: {...d.form, title: e.target.value}}))}
                className="rounded-sm mt-1"
                maxLength={80}
                data-testid="ai-rule-title-input"
              />
              <div className="text-[10px] text-zinc-400 mt-1">Judul singkat untuk mengingat aturan ini di daftar.</div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Instruksi ke AI <span className="text-red-500">*</span></label>
              <Textarea
                placeholder="Contoh: Kalau customer menyebut kata 'freon habis', tanyakan dulu apakah AC sudah pernah di-cleaning tahun ini sebelum mengusulkan isi freon."
                value={dlg.form.instruction}
                onChange={e => setDlg(d => ({...d, form: {...d.form, instruction: e.target.value}}))}
                rows={7}
                className="rounded-sm mt-1 text-[13px] leading-relaxed"
                maxLength={2000}
                data-testid="ai-rule-instruction-input"
              />
              <div className="text-[10px] text-zinc-400 mt-1 flex justify-between">
                <span>Tulis instruksi natural, tidak perlu format khusus. Akan dikirim ke AI apa adanya.</span>
                <span className="font-mono">{(dlg.form.instruction || "").length}/2000</span>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch
                checked={!!dlg.form.active}
                onCheckedChange={(v) => setDlg(d => ({...d, form: {...d.form, active: !!v}}))}
                data-testid="ai-rule-active-switch"
              />
              <div className="text-[12px]">
                <span className="font-semibold">{dlg.form.active ? "Aturan Aktif" : "Aturan Nonaktif"}</span>
                <span className="text-zinc-500"> — {dlg.form.active
                  ? "akan langsung digabung ke system prompt AI."
                  : "tersimpan tapi tidak dipakai AI."}
                </span>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={closeDlg} className="rounded-sm" data-testid="ai-rule-cancel-btn">Batal</Button>
              <Button onClick={save} className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm" data-testid="ai-rule-save-btn">
                Simpan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
